// Per-kind element renderers — the O(n), template-specific block renderers.
//
// This module IS the pluggable seam (project_editor_is_material_surface:
// per-template block renderers, O(n); the editor shell is O(1) and generic).
// MaterialDocument owns no element kind; each kind's body is drawn by an entry
// in this registry. A second template ships its OWN registry and reuses the
// shell unchanged — extension is adding a module here, not editing a switch in
// the shell.
//
// Each renderer receives its exact element variant (no cast — MaterialDocument's
// total dispatch narrows before calling) plus the pending proposal. A renderer
// only draws a FIELD-grain diff (scalar-edit / list-edit) inside its content,
// via InlineDiff; BLOCK-grain ops (add / remove / move) are the shell's job, so
// the renderer just draws its normal body for those. This split keeps the
// renderer ignorant of structural change — it renders content, the shell renders
// structure.
//
// Pure: props in, elements out. No state, no I/O.

import * as Limits from "@bilda/core/limits";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Heading } from "../Heading/Heading.js";
import { Icon } from "../Icon/Icon.js";
import { Prose } from "../Prose/Prose.js";
import { Text } from "../Text/Text.js";
import { EditableText } from "./EditableText/EditableText.js";
import { InlineDiff } from "./InlineDiff/InlineDiff.js";
import { applyRule, MARKDOWN_RULES } from "./inputRules.js";
import { Question } from "./Question/Question.js";
import "./renderers.css";
import { textKindOf } from "./textKind.js";
import type {
    DiffOp,
    ElementRendererProps,
    ElementRenderers,
    ElementRunProps,
    HeadingElement,
    McqElement,
    NoteElement,
    ObjectiveElement,
    ProseElement,
} from "./types.js";

// A field-grain edit the renderer should draw in place of the primary text.
// Block-grain ops return undefined (the shell wraps the body for those).
const fieldEditOf = (proposal?: {
    readonly op: DiffOp;
}): DiffOp | undefined => {
    if (!proposal) return undefined;
    const { op } = proposal;
    return op.type === "scalar-edit" ||
        op.type === "list-edit" ||
        op.type === "inline-edit"
        ? op
        : undefined;
};

const HeadingRenderer = ({
    element,
    proposal,
    onEditText,
    onDraftText,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<HeadingElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    return (
        // Shown as a Heading or a Subheading (pen Q76: two levels), whatever
        // level it is stored at (textKind.ts).
        <div
            className="bilda-render-heading"
            data-text-kind={textKindOf(element)}
        >
            <Heading level={element.level} size="xl">
                {edit ? (
                    <InlineDiff op={edit} />
                ) : onEditText ? (
                    <EditableText
                        value={element.text}
                        ariaLabel={t`Edit heading`}
                        onCommit={(text) => onEditText(element.id, text)}
                        onDraft={
                            onDraftText &&
                            ((text) => onDraftText(element.id, text))
                        }
                        onEnterAtEnd={onEnterAtEnd}
                        onBackspaceEmpty={onBackspaceEmpty}
                        autoEdit={autoEdit}
                        onAutoEditConsumed={onAutoEditConsumed}
                    />
                ) : (
                    element.text
                )}
            </Heading>
        </div>
    );
};

const ProseRenderer = ({
    element,
    proposal,
    onEditText,
    onDraftText,
    onInputRule,
    slashTriggerState,
    onSlashTrigger,
    onSlashNavigate,
    onSlashCommitActive,
    onEnterAtEnd,
    onBackspaceEmpty,
    onPasteTooLong,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<ProseElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    // A prose edit renders the before/after inline. Otherwise: an author-editable
    // material wires an EditableText over the raw markdown SOURCE, whose resting
    // face keeps the parsed Prose reading (renderDisplay) — you read the rendered
    // block, you edit the markdown. A read-only material just parses the markdown.
    if (edit) return <InlineDiff op={edit} />;
    return onEditText ? (
        <EditableText
            value={element.markdown}
            ariaLabel={t`Edit paragraph`}
            onCommit={(markdown) => onEditText(element.id, markdown)}
            onDraft={
                onDraftText && ((markdown) => onDraftText(element.id, markdown))
            }
            renderDisplay={(markdown) => <Prose>{markdown}</Prose>}
            // Written on the paper: the field takes the reading face's type,
            // and an empty paragraph says what it takes, in place.
            className="bilda-prose"
            placeholder={t`Write, or type / for more`}
            // The block's ceiling ({block}); a longer paste becomes a source.
            maxLength={Limits.BLOCK_TEXT_MAX}
            onPasteTooLong={onPasteTooLong}
            // Live markdown-rule + slash-trigger detection (only wired when the
            // host offers the callbacks — same progressive-disclosure discipline
            // as every other affordance here). `applyRule` needs THIS element to
            // build the destination (RuleContext) — that is why matching happens
            // in EditableText (a pure string→match lookup, no Element needed) but
            // the EFFECT is built here, one level up, where the ProseElement is
            // in scope.
            rules={onInputRule ? MARKDOWN_RULES : undefined}
            onRuleMatch={
                onInputRule
                    ? (match) =>
                          onInputRule(applyRule({ prose: element }, match))
                    : undefined
            }
            slashTriggerState={slashTriggerState}
            onSlashTrigger={onSlashTrigger}
            onSlashNavigate={onSlashNavigate}
            onSlashCommitActive={onSlashCommitActive}
            onEnterAtEnd={onEnterAtEnd}
            onBackspaceEmpty={onBackspaceEmpty}
            autoEdit={autoEdit}
            onAutoEditConsumed={onAutoEditConsumed}
        />
    ) : (
        <Prose>{element.markdown}</Prose>
    );
};

const ObjectiveRenderer = ({
    element,
    proposal,
    onEditText,
    onDraftText,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<ObjectiveElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    // One tight line of the run's list (ObjectivesRun names it once).
    return (
        <div className="bilda-render-objective">
            <Text as="div" className="bilda-render-objective__body">
                {edit ? (
                    <InlineDiff op={edit} />
                ) : onEditText ? (
                    <EditableText
                        value={element.text}
                        ariaLabel={t`Edit objective`}
                        onCommit={(text) => onEditText(element.id, text)}
                        onDraft={
                            onDraftText &&
                            ((text) => onDraftText(element.id, text))
                        }
                        onEnterAtEnd={onEnterAtEnd}
                        onBackspaceEmpty={onBackspaceEmpty}
                        autoEdit={autoEdit}
                        onAutoEditConsumed={onAutoEditConsumed}
                    />
                ) : (
                    element.text
                )}
            </Text>
        </div>
    );
};

// Above this many objectives the list folds behind its heading.
const OBJECTIVES_FOLD_AT = 5;

// A run of consecutive objectives as ONE compact "Learning objectives" list:
// a small heading, then the objectives as tight lines. A long list can be
// folded (open by default, so nothing is hidden on arrival).
const ObjectivesRun = ({ count, children }: ElementRunProps): ReactNode => {
    const { t } = useLingui();
    const heading = (
        <Text as="span" size="sm" weight="semibold" tone="dim">
            {t`Learning objectives`}
        </Text>
    );
    return count >= OBJECTIVES_FOLD_AT ? (
        <details className="bilda-render-objectives" open>
            <summary className="bilda-render-objectives__head">
                {heading}
            </summary>
            {children}
        </details>
    ) : (
        <section
            className="bilda-render-objectives"
            aria-label={t`Learning objectives`}
        >
            <div className="bilda-render-objectives__head">{heading}</div>
            {children}
        </section>
    );
};

// A quiz question: an object among the text, typed with plain keys
// (Question.tsx). A stem edit renders inline.
const McqRenderer = (props: ElementRendererProps<McqElement>): ReactNode => (
    <Question {...props} edit={fieldEditOf(props.proposal)} />
);

// A Note (pen "Always writing", piece 4): text for the author and the
// assistant, tinted, with a sticky-note mark and a line that says who sees
// it. Never shown to students and never exported (the domain drops it). Its
// text is bounded by the block ceiling; a longer paste keeps what fits (it
// is never taken in as a source, unlike a paragraph's).
const NoteRenderer = ({
    element,
    proposal,
    onEditText,
    onDraftText,
    onEnterAtEnd,
    onBackspaceEmpty,
    autoEdit,
    onAutoEditConsumed,
}: ElementRendererProps<NoteElement>): ReactNode => {
    const { t } = useLingui();
    const edit = fieldEditOf(proposal);
    return (
        <aside
            className="bilda-render-note"
            aria-label={t({ context: "block kind", message: "Note" })}
        >
            <Icon name="note" size="sm" />
            <div className="bilda-render-note__body">
                {edit ? (
                    <InlineDiff op={edit} />
                ) : onEditText ? (
                    <EditableText
                        value={element.text}
                        ariaLabel={t`Edit note`}
                        onCommit={(text) => onEditText(element.id, text)}
                        onDraft={
                            onDraftText &&
                            ((text) => onDraftText(element.id, text))
                        }
                        className="bilda-render-note__text"
                        placeholder={t`Write a note`}
                        maxLength={Limits.BLOCK_TEXT_MAX}
                        onEnterAtEnd={onEnterAtEnd}
                        onBackspaceEmpty={onBackspaceEmpty}
                        autoEdit={autoEdit}
                        onAutoEditConsumed={onAutoEditConsumed}
                    />
                ) : (
                    <div className="bilda-render-note__text">
                        {element.text}
                    </div>
                )}
                <div className="bilda-render-note__meta">
                    {t`Note · only you and the assistant see it`}
                </div>
            </div>
        </aside>
    );
};

// The default registry — one entry per kind. A new Element variant widens
// ElementRenderers and breaks this object until its renderer is written.
export const defaultRenderers: ElementRenderers = {
    heading: HeadingRenderer,
    prose: ProseRenderer,
    objective: ObjectiveRenderer,
    mcq: McqRenderer,
    note: NoteRenderer,
    runs: { objective: ObjectivesRun },
};
